import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { useDashboardData } from '../../hooks/useDashboardData';
import { useResponsive } from '../../hooks/useResponsive';
import './ReviewTrendChart.css';


interface ReviewMonthlyData {
  year_month: string;
  platforms: {
    [key: string]: {
      platform: string;
      total_reviews: number;
      average_rating: number;
    };
  };
}

interface ReviewMonthlyResponse {
  activePlatforms: string[];
  monthlyData: ReviewMonthlyData[];
}



const formatMonth = (ym: string): string => {
  const [y, m] = ym.split('-');
  return `${y}/${m}`;
};


const platformColors: { [key: string]: string } = {
  DOORDASH: '#FF3008',    
  UBER_EATS: '#06C167',   
  GMB: '#4285F4',         
  GRUBHUB: '#FF8000',     
  YELP: '#D32323'         
};



const platformDisplayNames: { [key: string]: string } = {
  DOORDASH: 'DoorDash',
  UBER_EATS: 'Uber Eats',
  GMB: 'Google',
  GRUBHUB: 'GrubHub',
  YELP: 'Yelp'
};


const platformOrder = ['GMB', 'YELP', 'UBER_EATS', 'DOORDASH', 'GRUBHUB'];


const ReviewTrendChart: React.FC = () => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<'cards' | 'trend'>('cards');
  const { isLaptop, isiPad, isiPhone } = useResponsive();

  
  const { data: dashboardData, loading, error } = useDashboardData();
  
  
  const reviewData = (dashboardData?.chartData as any)?.reviewMonthlyTrend as ReviewMonthlyResponse;

  
  const platformCardsData = useMemo(() => {
    if (!reviewData?.monthlyData?.length || !reviewData?.activePlatforms?.length) return [];

    
    const orderedPlatforms = platformOrder.filter(platform => 
      reviewData.activePlatforms.includes(platform)
    );

    return orderedPlatforms.map(platform => {
      
      const validMonths = reviewData.monthlyData.filter(month => 
        month.platforms[platform] && month.platforms[platform].total_reviews > 0
      );
      
      const totalReviews = validMonths.reduce((sum, month) => 
        sum + month.platforms[platform].total_reviews, 0
      );
      
      
      const avgMonthlyReviews = validMonths.length > 0 ? totalReviews / validMonths.length : 0;
      
      
      const ratingsData = validMonths
        .map(month => month.platforms[platform]?.average_rating)
        .filter(rating => rating !== null && rating !== undefined && rating > 0);
      
      const avgRating = ratingsData.length > 0 
        ? ratingsData.reduce((sum, rating) => sum + rating, 0) / ratingsData.length
        : null;

      
      const recentData = reviewData.monthlyData.slice(-2); 
      const recentReviews = recentData.map(month => month.platforms[platform]?.total_reviews || 0);
      const trend = recentReviews.length >= 2 
        ? recentReviews[1] - recentReviews[0]  
        : 0;

      return {
        platform,
        displayName: platformDisplayNames[platform] || platform,
        color: platformColors[platform] || '#666',
        totalReviews,
        avgMonthlyReviews: Math.round(avgMonthlyReviews),
        avgRating,
        trend,
        trendPercent: recentReviews[0] > 0 ? ((trend / recentReviews[0]) * 100) : 0
      };
    }); 
  }, [reviewData]);

  
  const chartOption = useMemo(() => {
    if (!reviewData?.monthlyData?.length || !reviewData?.activePlatforms?.length) return {};

    const months = reviewData.monthlyData.map(d => formatMonth(d.year_month));
    
    
    const orderedPlatforms = platformOrder.filter(platform => 
      reviewData.activePlatforms.includes(platform)
    );
    
    
    const reviewSeries = orderedPlatforms.map(platform => ({
      name: t(`reviewChart.platforms.${platform.toLowerCase()}`, { defaultValue: platformDisplayNames[platform] || platform }),
      type: 'bar',
      data: reviewData.monthlyData.map(m => 
        m.platforms[platform]?.total_reviews || 0
      ),
      itemStyle: { 
        color: platformColors[platform] || '#666',
        opacity: 0.8
      },
      yAxisIndex: 0,
      legendHoverLink: false
    }));

    
    const ratingSeries = orderedPlatforms.map(platform => ({
      name: t(`reviewChart.platforms.${platform.toLowerCase()}`, { defaultValue: platformDisplayNames[platform] || platform }),
      type: 'line',
      data: reviewData.monthlyData.map(m => 
        m.platforms[platform]?.average_rating || null
      ),
      lineStyle: { 
        width: isiPhone ? 2 : (isiPad ? 2.5 : (isLaptop ? 2.5 : 3)),
        color: platformColors[platform] || '#666'
      },
      itemStyle: { color: platformColors[platform] || '#666' },
      symbol: 'circle',
      symbolSize: isiPhone ? 4 : (isiPad ? 5 : (isLaptop ? 5 : 6)),
      yAxisIndex: 1,
      legendHoverLink: false
    }));

    return {
      grid: { 
        left: '3%', 
        right: '3%', 
        top: isiPhone ? '14%' : '10%', 
        bottom: isiPhone ? '5%' : '1%', 
        containLabel: true 
      },
      tooltip: { 
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ddd',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: isiPhone ? 10 : (isiPad ? 11 : (isLaptop ? 11 : 12))
        },
        formatter: function(params: any) {
          const monthStr = params[0].axisValue;
          let html = `<div style="padding: ${isiPhone ? '2px' : '3px'};">
            <div style="font-weight: bold; margin-bottom: ${isiPhone ? '4px' : '6px'}; font-size: ${isiPhone ? '10px' : '12px'};">${monthStr}</div>`;

          
          const reviewParams = params.filter((param: any) => param.seriesType === 'bar');
          const ratingParams = params.filter((param: any) => param.seriesType === 'line');

          
          if (reviewParams.length > 0) {
            html += `<div style="margin-bottom: 8px;">
              <div style="font-weight: bold; font-size: 11px; color: #666; margin-bottom: 4px;">📊 ${t('reviewChart.yAxisReviews')}</div>`;
            
            reviewParams.forEach((param: any) => {
              const platform = param.seriesName;
              const platformKey = orderedPlatforms.find(p => 
                t(`reviewChart.platforms.${p.toLowerCase()}`, { defaultValue: platformDisplayNames[p] || p }) === platform
              ) || '';
              const color = platformColors[platformKey] || '#666';
              html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
                <span style="margin-right: 10px; display: inline-flex; align-items: center;">
                  <span style="display: inline-block; width: 8px; height: 8px; background-color: ${color}; margin-right: 4px; border-radius: 50%;"></span>
                  <strong>${platform}:</strong>
                </span>
                <span style="font-weight: bold; font-size: 11px;">${param.value}${t('reviewChart.reviewsUnit')}</span>
              </div>`;
            });
            html += `</div>`;
          }

          
          if (ratingParams.length > 0) {
            html += `<div style="margin-bottom: 4px;">
              <div style="font-weight: bold; font-size: 11px; color: #666; margin-bottom: 4px;">⭐ ${t('reviewChart.yAxisRating')}</div>`;
            
            ratingParams.forEach((param: any) => {
              const platform = param.seriesName;
              const platformKey = orderedPlatforms.find(p => 
                t(`reviewChart.platforms.${p.toLowerCase()}`, { defaultValue: platformDisplayNames[p] || p }) === platform
              ) || '';
              const color = platformColors[platformKey] || '#666';
              const value = param.value ? `${param.value.toFixed(1)}${t('reviewChart.ratingUnit')}⭐` : 'N/A';
              html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
                <span style="margin-right: 10px; display: inline-flex; align-items: center;">
                  <span style="display: inline-block; width: 8px; height: 8px; background-color: ${color}; margin-right: 4px; border-radius: 50%;"></span>
                  <strong>${platform}:</strong>
                </span>
                <span style="font-weight: bold; font-size: 11px; color: #f39c12;">${value}</span>
              </div>`;
            });
            html += `</div>`;
          }
          
          html += `</div>`;
          return html;
        }
      },
      legend: { 
        show: true,
        top: isiPhone ? '0%' : '1%',
        left: 'center',
        data: orderedPlatforms.map(platform => 
          t(`reviewChart.platforms.${platform.toLowerCase()}`, { defaultValue: platformDisplayNames[platform] || platform })
        ),
        textStyle: { 
          fontSize: isiPhone ? 10 : (isiPad ? 12 : (isLaptop ? 13 : 14)),
          fontWeight: 'bold'
        },
        itemGap: isiPhone ? 8 : (isLaptop ? 12 : 15),
        itemWidth: isiPhone ? 12 : (isLaptop ? 14 : 16),
        itemHeight: isiPhone ? 6 : (isLaptop ? 8 : 10),
        selectedMode: true
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          fontSize: isiPhone ? 9 : (isiPad ? 10 : (isLaptop ? 11 : 12)),
          fontWeight: 'bold',
          rotate: months.length > 8 ? 45 : 0,
          margin: isiPhone ? 5 : (isiPad ? 6 : 8)
        }
      },
      yAxis: [
        {
          type: 'value',
          name: t('reviewChart.yAxisReviews'),
          position: 'left',
          nameTextStyle: { 
            fontSize: isiPhone ? 9 : (isiPad ? 10 : (isLaptop ? 11 : 12)), 
            fontWeight: 'bold',
            color: '#666'
          },
          axisLabel: { 
            fontSize: isiPhone ? 8 : (isiPad ? 9 : (isLaptop ? 10 : 11)),
            formatter: `{value}${t('reviewChart.reviewsUnit')}`
          },
          splitLine: { lineStyle: { opacity: 0.3 } }
        },
        {
          type: 'value',
          name: t('reviewChart.yAxisRating'),
          position: 'right',
          min: 0,
          max: 5,
          nameTextStyle: { 
            fontSize: isiPhone ? 9 : (isiPad ? 10 : (isLaptop ? 11 : 12)), 
            fontWeight: 'bold',
            color: '#666'
          },
          axisLabel: { 
            fontSize: isiPhone ? 8 : (isiPad ? 9 : (isLaptop ? 10 : 11)),
            formatter: `{value}${t('reviewChart.ratingUnit')}⭐`
          },
          splitLine: { show: false }
        }
      ],
      series: [...reviewSeries, ...ratingSeries]
    };
  }, [reviewData, t, isLaptop, isiPad, isiPhone]);

  
  if (loading) {
    return (
      <div className="review-trend-chart">
        <div className="review-trend-title-band">
          <h2>{t('reviewChart.title')}</h2>
        </div>
        <div className="review-trend-chart-container">
          <div className="review-trend-loading">
            <div className="review-trend-loading-spinner" />
            <div className="review-trend-loading-text" style={{ 
              fontSize: isiPhone ? '12px' : (isiPad ? '13px' : (isLaptop ? '14px' : '16px'))
            }}>
              {t('reviewChart.loading')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="review-trend-chart">
        <div className="review-trend-title-band">
          <h2>{t('reviewChart.title')}</h2>
        </div>
        <div className="review-trend-chart-container">
          <div className="review-trend-error">
            <div className="review-trend-error-icon">⚠️</div>
            <div className="review-trend-error-text" style={{ 
              fontSize: isiPhone ? '12px' : (isiPad ? '13px' : (isLaptop ? '14px' : '16px'))
            }}>
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!reviewData?.monthlyData?.length) {
    return (
      <div className="review-trend-chart">
        <div className="review-trend-title-band">
          <h2>{t('reviewChart.title')}</h2>
        </div>
        <div className="review-trend-chart-container">
          <div className="review-trend-error">
            <div className="review-trend-error-icon">📊</div>
            <div className="review-trend-error-text" style={{ 
              fontSize: isiPhone ? '12px' : (isiPad ? '13px' : (isLaptop ? '14px' : '16px'))
            }}>
              {t('reviewChart.noData')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="review-trend-chart">
      <div className="review-trend-title-band">
        <h2 style={{
          fontSize: isiPhone ? '14px' : (isiPad ? '15px' : (isLaptop ? '16px' : '18px')),
          padding: isiPhone ? '6px 15px' : (isiPad ? '7px 18px' : (isLaptop ? '8px 20px' : '10px 25px'))
        }}>{viewMode === 'cards' ? t('reviewChart.title') : t('reviewChart.monthlyTrendTitle')}</h2>
      </div>
      
      <div className="review-trend-chart-container">
        {}
        {viewMode === 'cards' && (
          <button 
            className="review-trend-monthly-button"
            onClick={() => setViewMode('trend')}
          >
            📈 {t('reviewChart.viewMonthlyTrend')}
          </button>
        )}
        {viewMode === 'cards' ? (
          
          <div className="review-platform-cards">
            {platformCardsData.map((platform) => (
              <div 
                key={platform.platform} 
                className="review-platform-card"
                style={{ 
                  borderColor: platform.color,
                  background: `linear-gradient(135deg, ${platform.color}08 0%, ${platform.color}03 100%)`
                }}
              >
                <div 
                  className="review-platform-card-header"
                  style={{ backgroundColor: platform.color }}
                >
                  <img 
                    src={`/${platform.platform.toLowerCase().replace('_', '')}-logo.png`}
                    alt={`${platform.displayName} logo`}
                    className="review-platform-card-logo"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <h3 className="review-platform-card-title">
                    {t(`reviewChart.platforms.${platform.platform.toLowerCase()}`, { defaultValue: platform.displayName })}
                  </h3>
                </div>
                
                <div 
                  className="review-platform-card-content"
                  style={{ 
                    background: `linear-gradient(135deg, ${platform.color}08 0%, ${platform.color}04 100%)` 
                  }}
                >
                  <div className="review-platform-metrics-grid">
                    <div className="review-platform-metric">
                      <div className="review-platform-metric-label">{t('reviewChart.metrics.monthlyAvgReviews')}</div>
                      <div className="review-platform-metric-value">
                        {platform.avgMonthlyReviews.toLocaleString()}<span className="reviews-unit">{t('reviewChart.reviewsUnit')}</span>
                      </div>
                    </div>
                    
                    <div className="review-platform-metric">
                      <div className="review-platform-metric-label">{t('reviewChart.metrics.monthlyAvgRating')}</div>
                      <div className="review-platform-metric-value rating">
                        {platform.avgRating ? `${platform.avgRating.toFixed(1)}${t('reviewChart.ratingUnit')}⭐` : 'N/A'}
                      </div>
                    </div>
                    
                    <div className="review-platform-metric">
                      <div className="review-platform-metric-label">{t('reviewChart.metrics.recentTrend')}</div>
                      <div className={`review-platform-metric-value trend ${
                        platform.trend > 0 ? 'positive' : platform.trend < 0 ? 'negative' : 'neutral'
                      }`}>
                        {platform.trend > 0 ? '↗️ +' : platform.trend < 0 ? '↘️ ' : '→ '}{Math.abs(platform.trendPercent).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                  
                  {}
                  <div className="review-platform-mini-chart">
                    <div className="review-platform-mini-chart-title">{t('reviewChart.metrics.recentMonthsTrend')}</div>
                    <div className="review-platform-mini-chart-content">
                      {reviewData.monthlyData.slice(-3).map((month, index) => {
                        const reviews = month.platforms[platform.platform]?.total_reviews || 0;
                        const maxReviews = Math.max(...reviewData.monthlyData.slice(-3).map(m => m.platforms[platform.platform]?.total_reviews || 0));
                        const height = maxReviews > 0 ? (reviews / maxReviews) * 100 : 0;
                        return (
                          <div 
                            key={index} 
                            className="review-platform-mini-bar"
                            title={`${formatMonth(month.year_month).split('/')[1]}${t('reviewChart.monthSuffix')}: ${reviews.toLocaleString()}${t('reviewChart.reviewsUnit')}`}
                          >
                            <div 
                              className="review-platform-mini-bar-fill"
                              style={{ 
                                height: `${height}%`,
                                backgroundColor: platform.color
                              }}
                            ></div>
                            <div className="review-platform-mini-bar-label">
                              {formatMonth(month.year_month).split('/')[1]}{t('reviewChart.monthSuffix')}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          
          <div className="review-trend-chart-content">
            <button 
              className="review-trend-back-button"
              onClick={() => setViewMode('cards')}
            >
              {t('reviewChart.backToOverview')}
            </button>
            <ReactECharts
              option={chartOption}
              style={{ 
                height: isiPhone ? '340px' : (isiPad ? '350px' : (isLaptop ? '380px' : '400px')), 
                width: '100%' 
              }}
              className="review-trend-echarts"
              opts={{ renderer: 'canvas' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewTrendChart;